import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { isViewAsRole, type ViewAsRole } from "@/config/view-as";
import { useProfile } from "@/context/ProfileContext";
import { useTeamScope } from "@/context/TeamScopeContext";
import { useSalesTeams } from "@/hooks/queries/use-sales-teams";
import { useIsDvhBusiness } from "@/hooks/use-business-vertical";

export type { ViewAsRole } from "@/config/view-as";

/** localStorage key prefix for the preview role, scoped per business. */
const STORAGE_PREFIX = "graftus_viewAs_";

interface ViewAsContextValue {
  /** The role the dashboard renders as: the preview when one is set, else the real one. */
  effectiveRole: ViewAsRole;
  /** The role the membership maps to, regardless of any preview. */
  realRole: ViewAsRole;
  /** The chosen preview, or null when the real role is shown. */
  viewAs: ViewAsRole | null;
  /** True for `super`/`adminchef`, the only ones who may preview another role. */
  canPreview: boolean;
  setViewAs: (role: ViewAsRole | null) => void;
}

const ViewAsContext = createContext<ViewAsContextValue | undefined>(undefined);

function readStored(businessId: string): ViewAsRole | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + businessId);
    return isViewAsRole(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * The "Visa som" state of the platform sketch (GRA-282): `super`/`adminchef`
 * see the whole dashboard and may preview it as any of the four store
 * roles; the preview is remembered per business and only ever applies to
 * them. Everyone else is pinned to the role their membership maps to
 * (`memberStoreRole`, from `effectiveStoreRole`) and can't switch.
 * Switching to the `admin` (department) preview seeds the active team with
 * the first department so the view has something to show. Sits inside
 * `TeamScopeProvider` and `ProfileProvider`.
 */
export function ViewAsProvider({ children }: { children: ReactNode }) {
  const { businessProfile, memberStoreRole } = useProfile();
  const businessId = businessProfile?.uid ?? "";
  const { activeTeamId, setActiveTeamId } = useTeamScope();
  const isDvh = useIsDvhBusiness();

  const canPreview = memberStoreRole === "super" || memberStoreRole === "adminchef";
  const realRole: ViewAsRole = memberStoreRole;

  const [stored, setStored] = useState<ViewAsRole | null>(null);
  const [hydratedBusinessId, setHydratedBusinessId] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId) {
      setStored(null);
      setHydratedBusinessId(null);
      return;
    }
    if (hydratedBusinessId === businessId) return;
    setStored(readStored(businessId));
    setHydratedBusinessId(businessId);
  }, [businessId, hydratedBusinessId]);

  const viewAs = canPreview && stored && stored !== realRole ? stored : null;
  const effectiveRole: ViewAsRole = viewAs ?? realRole;

  // Only needed to seed the admin (department) preview; report-only accounts never get here.
  const teamsQuery = useSalesTeams(
    canPreview && effectiveRole === "admin" && businessId ? businessId : undefined,
  );
  const firstTeamId = useMemo(() => {
    const teams = (teamsQuery.data ?? []) as Array<{ id: string; parentTeamId?: string | null }>;
    const departments = isDvh ? teams.filter((t) => t.parentTeamId) : [];
    return (departments.length > 0 ? departments : teams)[0]?.id ?? null;
  }, [teamsQuery.data, isDvh]);

  useEffect(() => {
    if (!canPreview || effectiveRole !== "admin") return;
    if (activeTeamId !== null || !firstTeamId) return;
    setActiveTeamId(firstTeamId);
  }, [canPreview, effectiveRole, activeTeamId, firstTeamId, setActiveTeamId]);

  const setViewAs = useCallback(
    (role: ViewAsRole | null) => {
      const next = role === realRole ? null : role;
      setStored(next);
      if (!businessId) return;
      try {
        if (next) localStorage.setItem(STORAGE_PREFIX + businessId, next);
        else localStorage.removeItem(STORAGE_PREFIX + businessId);
      } catch {
        // Private mode or blocked storage: the choice lives for the session.
      }
    },
    [businessId, realRole],
  );

  const value = useMemo<ViewAsContextValue>(
    () => ({ effectiveRole, realRole, viewAs, canPreview, setViewAs }),
    [effectiveRole, realRole, viewAs, canPreview, setViewAs],
  );

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

/**
 * The preview state. Outside the provider (older tests, storybook-like
 * renders) it reports the real role with no preview, so a page can read it
 * without caring whether the shell mounted the provider.
 */
export function useViewAs(): ViewAsContextValue {
  const ctx = useContext(ViewAsContext);
  if (ctx) return ctx;
  return FALLBACK;
}

const FALLBACK: ViewAsContextValue = {
  effectiveRole: "super",
  realRole: "super",
  viewAs: null,
  canPreview: false,
  setViewAs: () => {},
};
